/**
 * Shared math for the monthly statement views.
 *
 * Both the detailed daily statement (ReportDialogComponent) and the one-page
 * monthly statement (MonthlySummaryComponent) render from the same
 * `report/statement/html/` payload. Keeping the derived values here means the
 * two views can never disagree on a number.
 */

export interface StatementBuckets {
  all: any;
  pos: any;
  online: any;
  kiosk: any;
  thirdParty: any;
  paymentSummaryCardTotal: number;
  adjusted: StatementAdjusted;
}

export interface StatementAdjusted {
  adjTotalCollected: number;
  adjTotalCash: number;
  adjTotalPOSCash: number;
  adjTotalCard: number;
  adjTotalPOSCard: number;
  adjNetSales: number;
  adjOnlineNetSales: number;
  adjTaxes: number;
  adjStateTax: number;
  adjLocalTax: number;
  adjInStoreState: number;
  adjOnlineState: number;
  adjCashTax: number;
  adjCreditTax: number;
  adjVarianceDifference: number;
}

export interface StatementVarianceCheck {
  total_tenders: number;
  net_sales: number;
  taxes: number;
  additional_taxes: number;
  tips: number;
  gift_cards_sold: number;
  refunds: number;
  difference: number;
}

export interface StatementModel {
  m: StatementBuckets;
  varianceCheck: StatementVarianceCheck;
}

const nf = (obj: any, key: string): number => parseFloat(obj?.[key]) || 0;
const ni = (obj: any, key: string): number => parseInt(obj?.[key], 10) || 0;
const round2 = (n: number): number => Math.round(n * 100) / 100;
const share = (part: number, total: number): number => total === 0 ? 0 : part / total;

const KIOSK_MONEY_FIELDS = ['gross_sales', 'discounts', 'refund_net_value', 'net_sales', 'net_taxable_sales',
  'non_taxable_sales', 'cash', 'card', 'total_receipts', 'cash_taxable_sales',
  'credit_taxable_sales', 'non_taxable_sale_cash', 'non_taxable_sale_card',
  'taxes', 'state_tax', 'local_tax', 'additional_tax', 'tips', 'auto_gratuity', 'auto_gratuity_pos', 'auto_gratuity_online', 'delivery_fees', 'platform_charges',
  'credit_tips', 'credit_keyed', 'third_party'];
const KIOSK_COUNT_FIELDS = ['total_receipts_count', 'total_orders', 'discounts_count', 'refund_quantity'];

export function buildStatementModel(statement: any): StatementModel {
  const miniReport = statement?.mini_report || {};
  const a = miniReport.all || {};
  const p = miniReport.on_premise || {};
  const o = miniReport.online || {};
  const t = miniReport.third_party || {};

  // Compute kiosk as All - POS - Online - Third-party apps (on_premise leaves the apps out)
  const kiosk: any = {};
  KIOSK_MONEY_FIELDS.forEach(f => {
    kiosk[f] = Math.max(nf(a, f) - nf(p, f) - nf(o, f) - nf(t, f), 0);
  });
  KIOSK_COUNT_FIELDS.forEach(f => {
    kiosk[f] = Math.max(ni(a, f) - ni(p, f) - ni(o, f) - ni(t, f), 0);
  });
  kiosk.average_ticket_value = kiosk.total_receipts_count > 0
    ? kiosk.total_receipts / kiosk.total_receipts_count : 0;

  // Compute adjusted values (same logic as reporting-home computeAdjusted)
  const refunds = nf(a, 'refund_net_value');
  const totalReceipts = nf(a, 'total_receipts');
  // Only online redemptions count as an extra tender: the online card charge is
  // reduced by the redeemed balance, so it is missing from total_receipts. In-store
  // redemptions are already inside total_receipts. Same rule as the backend's
  // detailed statement and the daily sales email.
  const giftRedeemedOnline = nf(a, 'total_gift_card_redeemed_online');
  const tendersNetOfRefunds = Math.max(0, totalReceipts + giftRedeemedOnline - refunds);
  // Additional taxes (liquor / mixed beverage tax) are collected beside sales tax and
  // reported on their own line; `taxes` keeps meaning sales tax.
  const additionalTax = nf(a, 'additional_tax');
  const expectedCollected = nf(a, 'net_sales') + nf(a, 'taxes') + additionalTax + nf(a, 'tips');
  const baseVariance = tendersNetOfRefunds - expectedCollected;
  const varianceRounded = round2(baseVariance);
  const taxFix = (Math.abs(varianceRounded) < 0.05 && varianceRounded !== 0) ? varianceRounded : 0;

  const allCard = nf(a, 'card');
  const inStoreGCCredit = nf(a, 'total_gift_card_sold_instore_credit');
  const onlineGCCredit = nf(a, 'total_gift_card_sold_online');
  const splitCreditCard = nf(p, 'split_credit');
  const allSurcharge = nf(a, 'surcharge');
  const onlineSurcharge = nf(o, 'surcharge');
  const cardTotal = round2(allCard + inStoreGCCredit + onlineGCCredit + splitCreditCard);
  const taxes = nf(a, 'taxes');
  const totalGiftSold = nf(a, 'total_value_sold');
  // ENG-5983: `cash` already holds gift cards sold for cash; it leaves out the cash legs of
  // split checks, which cardTotal gets as split_credit.
  const splitCash = nf(p, 'split_cash');
  const giftCardSoldCard = nf(a, 'total_gift_card_sold_instore_credit');

  const stateTotal = nf(a, 'state_tax');
  const localTotal = nf(a, 'local_tax');
  const stateInStore = nf(p, 'state_tax');
  const stateOnline = nf(o, 'state_tax');
  const taxCash = nf(a, 'tax_collected_cash');
  const taxCredit = nf(a, 'tax_collected_credit');

  const stateLocalTotal = Math.max(0, stateTotal + localTotal);
  const stateFix = round2(taxFix * share(stateTotal, stateLocalTotal));
  const localFix = round2(taxFix - stateFix);
  const stateSplitTotal = Math.max(0, stateInStore + stateOnline);
  const inStoreStateFix = round2(stateFix * share(stateInStore, stateSplitTotal));
  const onlineStateFix = round2(stateFix - inStoreStateFix);
  const payTypeTotal = Math.max(0, taxCash + taxCredit);
  const cashTaxFix = round2(taxFix * share(taxCash, payTypeTotal));
  const creditTaxFix = round2(taxFix - cashTaxFix);

  const varianceDifference = (totalReceipts + totalGiftSold + giftRedeemedOnline)
    - (nf(a, 'net_sales') + allSurcharge)
    - (taxes + taxFix) - additionalTax - nf(a, 'tips') - totalGiftSold - refunds;

  const adjusted: StatementAdjusted = {
    adjTotalCollected: round2(totalReceipts + totalGiftSold),
    adjTotalCash: round2(nf(a, 'cash') + splitCash),
    adjTotalPOSCash: round2(nf(p, 'cash') + splitCash),
    adjTotalCard: cardTotal,
    adjTotalPOSCard: round2(nf(p, 'card') + giftCardSoldCard),
    adjNetSales: round2(nf(a, 'net_sales') + allSurcharge),
    adjOnlineNetSales: round2(nf(o, 'net_sales') + onlineSurcharge),
    adjTaxes: round2(taxes + taxFix),
    adjStateTax: round2(stateTotal + stateFix),
    adjLocalTax: round2(localTotal + localFix),
    adjInStoreState: round2(stateInStore + inStoreStateFix),
    adjOnlineState: round2(stateOnline + onlineStateFix),
    adjCashTax: round2(taxCash + cashTaxFix),
    adjCreditTax: round2(taxCredit + creditTaxFix),
    adjVarianceDifference: round2(varianceDifference),
  };

  // Variance check display values (matching reporting-home)
  const varianceCheck: StatementVarianceCheck = {
    total_tenders: round2(totalReceipts + totalGiftSold + giftRedeemedOnline),
    net_sales: adjusted.adjNetSales,
    taxes: adjusted.adjTaxes,
    additional_taxes: round2(additionalTax),
    tips: nf(a, 'tips'),
    gift_cards_sold: totalGiftSold,
    refunds: refunds,
    difference: adjusted.adjVarianceDifference,
  };

  return {
    m: { all: a, pos: p, online: o, kiosk, thirdParty: t, paymentSummaryCardTotal: cardTotal, adjusted },
    varianceCheck,
  };
}
